/**
 * Passo de consentimento COMUM às duas modalidades (#93, D-063 item 3) — a
 * manifestação de vontade é a mesma na Simples e na Qualificada (mesmo termo,
 * mesma rota `POST /v1/signers/:id/consent`, mesmo evento probatório
 * `consent_given`); o que muda entre modalidades é a AUTENTICAÇÃO (OTP vs posse
 * da chave), não o consentimento.
 *
 * Invariante #91: "Li e concordo" SÓ é clicável com o documento carregado no
 * viewer (`viewerReady`) — sem documento em tela a declaração seria falsa. O
 * hash aparece como âncora de integridade (metadado), nunca como substituto.
 */
import { Button, Card, Checkbox } from '@argink/ui';
// Subpath browser-safe (DI2-1): o INDEX de @argink/shared reexporta webhook-hmac,
// que puxa node:crypto para o bundle — import de VALOR só pelos subpaths.
import { CONSENT_TERMS_VERSION } from '@argink/shared/constants';
import type { EnvelopeView, SignerView } from '@argink/shared';
import { StateCard } from './StateCard';
import type { HumanError } from '../lib/errors';

export interface ConsentCardProps {
  envelope: EnvelopeView;
  signer: SignerView;
  /** `true` quando o PdfViewer reportou 'loaded' — precondição do aceite (#91). */
  viewerReady: boolean;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  onAccept: () => void;
  busy: boolean;
  error: HumanError | null;
  /** Número do passo exibido no título (a ordem difere entre modalidades). */
  stepNumber: number;
}

export function ConsentCard({
  envelope,
  signer,
  viewerReady,
  checked,
  onCheckedChange,
  onAccept,
  busy,
  error,
  stepNumber,
}: ConsentCardProps) {
  const isSimples = signer.required_signature_level === 'simples';
  return (
    <Card className="consent-card" aria-labelledby="consent-title">
      <h2 id="consent-title" className="card__title">
        {stepNumber}. Consentimento para assinar
      </h2>
      {isSimples ? (
        <p>
          Esta é uma assinatura eletrônica simples: seu canal de e-mail foi verificado e seu aceite
          será registrado como manifestação de vontade para este documento.
        </p>
      ) : (
        <p>
          Seu aceite será registrado como manifestação de vontade para este documento. Em seguida
          você escolhe o seu certificado digital ICP-Brasil para assinar.
        </p>
      )}
      <p>
        <strong>Hash SHA-256 do documento original:</strong>{' '}
        <span className="mono mono--wrap">{envelope.documents[0]?.hash_sha256 ?? '—'}</span>
      </p>
      {isSimples ? (
        <p>O selo criptográfico aplicado ao PDF pertence à plataforma ARGINK; ele não é um certificado digital seu.</p>
      ) : null}
      <p className="card__note">Termo {CONSENT_TERMS_VERSION}</p>
      {!viewerReady ? (
        <p className="consent-card__gate card__note" role="status">
          O aceite fica disponível quando o documento terminar de carregar ao lado — a declaração
          &quot;li e concordo&quot; exige o documento em tela.
        </p>
      ) : null}
      <Checkbox
        id="consent-check"
        label="Li e concordo em assinar este documento."
        checked={checked}
        disabled={!viewerReady}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      <Button
        variant="primary"
        disabled={!viewerReady || !checked || busy}
        isLoading={busy}
        onClick={onAccept}
      >
        Li e concordo em assinar
      </Button>
      {error ? <StateCard tone="error" title={error.title} detail={error.detail} traceId={error.traceId} /> : null}
    </Card>
  );
}
